import React, { useState } from 'react';
import LoadingIndicator from '../LoadingIndicator';
import styles from './DossierButton.scss';
import ValuationApi from './ValuationApi';
import { IValuationData } from './ValuationWidget';
import winOpen from '../../utils/openWindowWithLoadingIndicator';
import ValuationRequest from '../../utils/ValuationRequest';
import { createToast } from '../MaterialUi/Toast';
import LogError from '../../utils/LogError';

export interface IDossierButtonProps {
    className?: string;
    uuid: string;
    data: IValuationData;
    dossierUrl: string;
}

/* 
   window.open in iOS Safari doesn't work in async functions, so window.open must called before all requests 
   see https://stackoverflow.com/questions/13636776/javascript-cant-open-new-tab-in-safari  
*/
let win = null as Window;

/**
 * create a pricehubble dossier link of a valuation dashboard property
 */
const DossierButton: React.FC<React.PropsWithChildren<IDossierButtonProps>> = ({ uuid: id, data, dossierUrl, className = '', children }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [currDossierUrl, setCurrDossierUrl] = useState<string>(dossierUrl);

    const handleClick = () => {
        setIsLoading(true);
        win = winOpen();
        ValuationApi.createDossier({ ...data, reason: 'sell' })
            .then((res) => {
                const dossierData = res.getData();
                const dossierId = dossierData?.id ? dossierData.id : data.dossier_id;
                return ValuationApi.getDossierLink(dossierId).then((url) => {
                    // Drupal don't like ISO ISO_8601 Date, but short form without Milliseconds
                    const dossier_url_valid_until = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toJSON();

                    win?.location?.assign(url);
                    win?.focus();

                    ValuationRequest.patch(id, {
                        // id: data.id,
                        dossier_id: dossierId,
                        dossier_url: url,
                        dossier_url_valid_until,
                    });
                    setCurrDossierUrl(url);
                });
            })
            .catch((e) => {
                win?.close();
                console.error(e);
                if (typeof e === 'string') {
                    createToast(e, 'error');
                } else {
                    createToast('Fehler beim Erstellen des Dossiers.', 'error');
                }
                LogError.add(e, {
                    title: 'Fehler beim Erstellen des Dossiers',
                    error_code: 'DossierButton.tsx:handleClick',
                    request_data: {
                        dossierId: data.dossier_id,
                        dossierUrl: data.dossier_url,
                        dossierUrlValidUntil: data.dossier_url_valid_until,
                        uuid: id,
                    },
                });
            })
            .then(() => {
                setIsLoading(false);
            });
    };

    return (
        <a
            className={styles.dossierButton + ' ' + className}
            href={currDossierUrl}
            target={currDossierUrl ? '_blank' : null}
            onClick={currDossierUrl ? null : handleClick}
        >
            {isLoading ? <LoadingIndicator size="small" /> : children}
        </a>
    );
};

export default DossierButton;
